import axios from 'axios';
import {
  FETCH_PLATFORM_ALERTS,
  PLATFORM_ALERTS_SUCCESS,
  PLATFORM_ALERTS_ERROR,
} from '../actions/platformAlerts';
import { AUTH_LOGOUT } from '../actions/auth';

/**
 * What the platform header should be shouting about: a lapsed subscription, a WhatsApp number
 * that dropped.
 *
 * In Vuex rather than in the layout because InsuranceNotifications — the global websocket
 * listener — is a SIBLING of the layout, not a child, and needs somewhere shared to poke when a
 * relevant event arrives.
 *
 * The backend has already filtered these to what the current user may see (a colleague's personal
 * WhatsApp seat is not in the payload at all), so the banner renders whatever it is handed.
 */
const state = {
  alerts: [],
  status: '',
  // Monotonic request id. Several things refetch alerts — a 5-minute poll, a websocket event, tab
  // focus — so two requests can easily be in flight at once, and they do not necessarily come back
  // in order. Without this a slow "disconnected" response landing after a fast "reconnected" one
  // would put the banner back up for another five minutes.
  requestSeq: 0,
};

const getters = {
  platformAlerts: (state) => state.alerts,
  platformAlertsLoaded: (state) => state.status === 'success',
};

const actions = {
  [FETCH_PLATFORM_ALERTS]: ({ commit, state }) => {
    const seq = state.requestSeq + 1;
    commit(FETCH_PLATFORM_ALERTS, seq);
    return axios.post('/insurance/platform-alert/list', {})
      .then((resp) => {
        commit(PLATFORM_ALERTS_SUCCESS, { seq, items: resp.data && resp.data.items });
        return resp;
      })
      .catch((err) => {
        commit(PLATFORM_ALERTS_ERROR, seq);
        return Promise.reject(err);
      });
  },
};

const mutations = {
  [FETCH_PLATFORM_ALERTS]: (state, seq) => {
    state.requestSeq = seq;
    state.status = 'loading';
  },
  [PLATFORM_ALERTS_SUCCESS]: (state, { seq, items }) => {
    // A response older than the newest request we issued is stale — drop it rather than let it
    // resurrect a banner that a later request already cleared.
    if (seq !== state.requestSeq) return;
    state.status = 'success';
    state.alerts = Array.isArray(items) ? items : [];
  },
  // Deliberately keeps the previous alerts. A failed poll is not evidence that a lapse has been
  // paid, and blanking the strip on a dropped request would tell the user the opposite.
  //
  // That reasoning covers a dropped request; it does NOT cover a change of who is looking, which
  // is what AUTH_LOGOUT below is for.
  [PLATFORM_ALERTS_ERROR]: (state, seq) => {
    if (seq !== state.requestSeq) return;
    state.status = 'error';
  },
  /**
   * Wipe on logout.
   *
   * Logging out is a SPA navigation, not a reload, so this module's state outlives the session
   * (switching WORKSPACE does reload, which is why that case needs nothing). Without this, the
   * next person to log in on the same machine sees the previous user's alerts until their own
   * fetch returns — and a personal WhatsApp alert carries a phone number, so this is the exact
   * cross-user leak the endpoint's server-side scoping exists to prevent.
   */
  [AUTH_LOGOUT]: (state) => {
    state.alerts = [];
    state.status = '';
    // Bumped, not reset: an in-flight request issued by the previous session must not be able to
    // match on seq and write its result into the new one.
    state.requestSeq += 1;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
